import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { filter, Observable, of, ReplaySubject, take, takeUntil } from 'rxjs';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import {
  VehicleGroupNameDialogComponent
} from '../pages/database/pages/vehicles/dialogs/group-name-dialog/vehicle-group-name-dialog.component';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { Store } from '@ngrx/store';
import {
  AddVehiclesToGroupsDialogComponent
} from '../pages/database/pages/vehicles/dialogs/add-vehicles-to-groups-dialog/add-vehicles-to-groups-dialog.component';
import {
  AddVehicleToGroupDialogComponent
} from '../pages/database/pages/vehicles/dialogs/add-vehicle-to-group-dialog/add-vehicle-to-group-dialog.component';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { JoinGroupDialogComponent } from '../pages/database/pages/vehicles/dialogs/join-group-dialog/join-group-dialog.component';
import {
  ShareVehicleGroupDialogComponent
} from '../pages/database/pages/vehicles/dialogs/share-vehicle-group-dialog/share-vehicle-group-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class VehicleGroupsDialogService {
  private store$ = inject(Store);
  private dialog = inject(MatDialog);
  private actions$ = inject(Actions);

  private joinDialogRef: MatDialogRef<JoinGroupDialogComponent> | null = null;
  private isNameDialogOpen = false;

  public openCreate(): Observable<VehicleGroupsModel.VehicleGroupListDocument | null> {
    if (this.isNameDialogOpen) {
      return of(null);
    }
    this.isNameDialogOpen = true;
    const createdGroup$ = new ReplaySubject<VehicleGroupsModel.VehicleGroupListDocument | null>(1);
    let createdGroupEmitted = false;
    let createInProgress = false;

    const dialogRef = this.dialog.open(VehicleGroupNameDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
        if (!createdGroupEmitted) {
          createdGroup$.next(null);
          createdGroup$.complete();
        }
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((name: string) => {
        if (createInProgress) {
          return;
        }

        this.store$.select(vehicleGroupsFeature.selectAllVehicleGroups).pipe(take(1)).subscribe((all) => {
          if (VehicleGroupsModel.isDuplicateVehicleGroupName(name, all)) {
            dialogRef.componentInstance.setDuplicateError(name);
            return;
          }
          const normalizedName = name.trim().toLowerCase();
          createInProgress = true;

          this.actions$
            .pipe(
              ofType(VehicleGroupsActions.createVehicleGroupSuccess),
              filter(({ vehicleGroup }) => vehicleGroup.name.trim().toLowerCase() === normalizedName),
              take(1),
              takeUntil(dialogRef.afterClosed()),
            )
            .subscribe(({ vehicleGroup }) => {
              createdGroupEmitted = true;
              createdGroup$.next(vehicleGroup);
              createdGroup$.complete();
              dialogRef.close();
            });

          this.actions$
            .pipe(
              ofType(VehicleGroupsActions.createVehicleGroupFail),
              take(1),
              takeUntil(dialogRef.afterClosed()),
            )
            .subscribe(() => {
              createInProgress = false;
            });

          this.store$.dispatch(VehicleGroupsActions.createVehicleGroup({ name }));
        });
      });

    return createdGroup$.asObservable();
  }

  public openAddVehicleToGroups(
    data: VehicleGroupsModel.AddVehicleToGroupsDialogData,
  ): MatDialogRef<AddVehiclesToGroupsDialogComponent, boolean> {
    return this.dialog.open<
      AddVehiclesToGroupsDialogComponent,
      VehicleGroupsModel.AddVehicleToGroupsDialogData,
      boolean
    >(AddVehiclesToGroupsDialogComponent, {
      width: '560px',
      panelClass: 'modal-no-padding',
      disableClose: false,
      data,
    });
  }

  /**
   * Opens join-group dialog. When another vehicle-group modal is already open (non-join), returns null.
   * If the join dialog is already open, it is closed first so a route-driven reopen can apply a new invite code.
   */
  public openJoinDialog(
    routeParams?: Record<string, string>,
  ): MatDialogRef<JoinGroupDialogComponent> | null {
    const raw = routeParams?.[VehicleGroupsModel.JoinRouteQueryParam.InviteCode];
    const initialCode =
      raw != null && String(raw).trim() !== '' ? String(raw).trim() : undefined;
    const data: VehicleGroupsModel.JoinGroupDialogData | undefined = initialCode
      ? { initialCode }
      : undefined;

    if (this.joinDialogRef) {
      this.joinDialogRef.close();
    }

    this.isNameDialogOpen = true;
    const dialogRef = this.dialog.open(JoinGroupDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });
    this.joinDialogRef = dialogRef;

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.joinDialogRef === dialogRef) {
          this.joinDialogRef = null;
          this.isNameDialogOpen = false;
        }
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((code: string) => {
        this.store$.dispatch(VehicleGroupsActions.joinVehicleGroup({ code }));
        dialogRef.close();
      });

    return dialogRef;
  }

  public openShare(groupId: string, groupName: string, createdByOrgId: string): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;

    const data: VehicleGroupsModel.ShareGroupDialogData = {
      groupId,
      groupName,
      createdByOrgId,
    };

    const dialogRef = this.dialog.open(ShareVehicleGroupDialogComponent, {
      width: '660px',
      height: '80vh',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
        this.store$.dispatch(VehicleGroupsActions.clearVehicleGroupShareOptions());
      });
  }

  public openEdit(groupId: string, currentName: string): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;

    const data: VehicleGroupsModel.GroupNameDialogData = {
      initialName: currentName,
      titleKey: 'VEHICLE_GROUPS.NAME_DIALOG.EDIT_TITLE',
      submitKey: 'COMMON.SAVE',
    };

    const dialogRef = this.dialog.open(VehicleGroupNameDialogComponent, {
      width: '520px',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
      });

    dialogRef.componentInstance.continueClicked
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((name: string) => {
        this.store$.select(vehicleGroupsFeature.selectAllVehicleGroups).pipe(take(1)).subscribe((all) => {
          if (VehicleGroupsModel.isDuplicateVehicleGroupName(name, all, groupId)) {
            dialogRef.componentInstance.setDuplicateError(name);
            return;
          }
          this.store$.dispatch(VehicleGroupsActions.updateVehicleGroup({ id: groupId, name }));
          dialogRef.close();
        });
      });
  }

  public openAddVehiclesToGroup(
    groupId: string,
    groupName: string,
    vehicleGroupMemberIds: number[],
  ): void {
    if (this.isNameDialogOpen) {
      return;
    }
    this.isNameDialogOpen = true;
    this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));
    this.store$.dispatch(VehiclesActions.getVehicles());
    this.store$.dispatch(VehiclesActions.getUnsavedVehicles());

    const data: VehicleGroupsModel.AddVehicleToGroupDialogData = {
      groupId,
      groupName,
      vehicleGroupMemberIds,
    };

    const dialogRef = this.dialog.open(AddVehicleToGroupDialogComponent, {
      width: '1000px',
      height: '80vh',
      panelClass: 'modal-no-padding',
      disableClose: true,
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.isNameDialogOpen = false;
      });
  }
}